import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronLeftIcon, EnterIcon } from '@radix-ui/react-icons';
import { Button, Card, Flex, Spinner } from '@radix-ui/themes';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SelectComponent from '../../components/inputs/Select/SelectComponent';
import TextFieldComponent from '../../components/inputs/Textfield/TextfieldComponent';
import { useAppDispatch } from '../../hooks/hooks';
import { useGetFacultiesQuery } from '../../hooks/useGetFacultiesQuery';
import { resetUpdateDepartamentData } from '../../redux/slices/school/departament';
import { RootState } from '../../redux/store';
import { routes } from '../../routes';
import { updateDepartament } from '../../services/school/departament/updateDepartament';
import { UpdateDepartamentType } from '../../types/Departament';
import { UpdateDepartamentFormValidation } from './UpdateDepartamentFormValidation';
import TextAreaFieldComponent from '../../components/inputs/TextAreafield/TextAreafieldComponent';
import { useGetUniversitiesQuery } from '../../hooks/useGetUniversitiesQuery';
import { provinces } from '../../utils/codes';

export const UpdateDepartament = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const mutation = useMutation({ mutationFn: updateDepartament, mutationKey: "updateDepartament" })
  const departamentUpdateFormData = useSelector((state: RootState) => state.departament);
  const queryClient = useQueryClient();

  const { control, handleSubmit, formState: { errors }, reset, getValues, setValue } = useForm<UpdateDepartamentType>({
    resolver: yupResolver(UpdateDepartamentFormValidation),
    defaultValues: departamentUpdateFormData,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,
  })
  const { data: universities, isLoading: isLoadingUniversities, isError: errorUniversities, refetch: refetchUniversities } = useGetUniversitiesQuery({ PageNumber: 1, PageSize: 400 });
  const { data: faculties, isLoading: isLoadingFaculties, isError: errorFaculties, refetch: refetchFaculties } = useGetFacultiesQuery({ PageNumber: 1, PageSize: 400, IdUniversity: getValues('idUniversity') });
  const onSubmit = handleSubmit(async (data) => {
    try {
      const result = await mutation.mutateAsync(data);
      if (result) {
        toast.success('Departamento actualizado com sucesso!');
        queryClient.invalidateQueries('departaments-pagination');
        navigate(routes.departaments);
      }
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });
  const handleBack = (e: any) => {
    dispatch(resetUpdateDepartamentData());
    navigate(routes.departaments);
  }
  const handleUniversityChange = (id: string) => {
    setValue('idUniversity', id);
    setValue('idFaculty', '');
    refetchFaculties();
  };

  return (
    <form onSubmit={onSubmit} noValidate>
      <Card size='1'>
        <Flex direction='column' gap='4'>
          <TextFieldComponent name="description" label='Departamento' control={control} errors={errors} fieldProps={{ placeholder: 'Nome do Departamento' }} required={true} />
          <SelectComponent
            name="idUniversity"
            label="Instituição de Ensino Superior"
            control={control}
            errors={errors}
            style={{ width: '100%' }}
            options={universities?.items.map(u => ({
              value: u.id ?? "",
              label: u.description ?? ""
            }))}
            itemsLoading={isLoadingUniversities}
            itemsError={errorUniversities}
            onSelect={handleUniversityChange}
            required={true}
          />
          <SelectComponent name="idFaculty" label='Unidade orgânica' control={control} errors={errors} style={{ width: '100%' }}
            options={faculties?.items.map(faculty => ({
              value: faculty.id ?? "",
              label: faculty.description ?? ""
            }))}
            required={true}
          />

          <SelectComponent
            name="province"
            label="Provincia"
            required={true}
            control={control}
            errors={errors}
            style={{ width: '100%' }}
            options={provinces?.map(u => ({
              value: u.id ?? "",
              label: u.description ?? ""
            }))}
          />
          <TextAreaFieldComponent name="address" label="Endereço" control={control} errors={errors} fieldProps={{ placeholder: 'Endereço' }} />
          <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
            <Button type="button" variant="soft" color="gray" onClick={handleBack} disabled={mutation.isLoading}>
              <ChevronLeftIcon /> Voltar
            </Button>
            <Button type="submit" disabled={mutation.isLoading} variant='surface'  >
              {mutation.isLoading ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
              Registar
            </Button>
          </Flex>
        </Flex>
      </Card>
    </form>
  )
}